import type {
  BulkMoveTestCasesInput,
  CreateTestCaseInput,
  ImportTestCasesInput,
  ListTestCasesQuery,
  TestCaseDto,
  TestCaseImportResultDto,
  TestCaseListItemDto,
  UpdateTestCaseInput,
} from '@tms/shared';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api-client';
import { queryKeys } from '../../lib/query-keys';

export type TestCaseListParams = Partial<
  Pick<
    ListTestCasesQuery,
    'folderId' | 'priority' | 'severity' | 'status' | 'labelId' | 'q' | 'sort' | 'order'
  >
> & { page?: number; perPage?: number; includeSubfolders?: boolean };

export const testCasesQueryOptions = (projectCode: string, params: TestCaseListParams) =>
  queryOptions({
    queryKey: queryKeys.testCases.list(projectCode, params),
    queryFn: ({ signal }) =>
      api.list<TestCaseListItemDto>(`/projects/${projectCode}/test-cases`, { ...params }, signal),
    placeholderData: (previous) => previous,
  });

export const testCaseQueryOptions = (projectCode: string, caseKey: string) =>
  queryOptions({
    queryKey: queryKeys.testCases.detail(projectCode, caseKey),
    queryFn: ({ signal }) =>
      api.get<TestCaseDto>(`/projects/${projectCode}/test-cases/${caseKey}`, undefined, signal),
  });

/** `enabled` lets a closed dialog mount its picker without fetching. */
export const useTestCases = (
  projectCode: string,
  params: TestCaseListParams,
  options: { enabled?: boolean } = {},
) => useQuery({ ...testCasesQueryOptions(projectCode, params), enabled: options.enabled ?? true });

export const useTestCase = (projectCode: string, caseKey: string) =>
  useQuery(testCaseQueryOptions(projectCode, caseKey));

const useCaseInvalidation = (projectCode: string) => {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.testCases.all(projectCode) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.folders.tree(projectCode, 'case') });
    void queryClient.invalidateQueries({ queryKey: queryKeys.projects.overview(projectCode) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
  };
};

export const useCreateTestCase = (projectCode: string) => {
  const invalidate = useCaseInvalidation(projectCode);
  return useMutation({
    mutationFn: (input: CreateTestCaseInput) =>
      api.post<TestCaseDto>(`/projects/${projectCode}/test-cases`, input),
    onSuccess: invalidate,
  });
};

export const useUpdateTestCase = (projectCode: string, caseId: string) => {
  const invalidate = useCaseInvalidation(projectCode);
  return useMutation({
    mutationFn: (input: UpdateTestCaseInput) =>
      api.patch<TestCaseDto>(`/projects/${projectCode}/test-cases/${caseId}`, input),
    onSuccess: invalidate,
  });
};

export const useDuplicateTestCase = (projectCode: string) => {
  const invalidate = useCaseInvalidation(projectCode);
  return useMutation({
    mutationFn: (caseId: string) =>
      api.post<TestCaseDto>(`/projects/${projectCode}/test-cases/${caseId}/duplicate`),
    onSuccess: invalidate,
  });
};

export const useDeleteTestCase = (projectCode: string) => {
  const invalidate = useCaseInvalidation(projectCode);
  return useMutation({
    mutationFn: (caseId: string) => api.delete(`/projects/${projectCode}/test-cases/${caseId}`),
    onSuccess: invalidate,
  });
};

export const useMoveTestCases = (projectCode: string) => {
  const invalidate = useCaseInvalidation(projectCode);
  return useMutation({
    mutationFn: (input: BulkMoveTestCasesInput) =>
      api.post<{ moved: number }>(`/projects/${projectCode}/test-cases/bulk-move`, input),
    onSuccess: invalidate,
  });
};

/**
 * Creates the test cases read out of an export file. The file itself never
 * leaves the browser: the dialog reads it, maps its columns and sends cases.
 */
export const useImportTestCases = (projectCode: string) => {
  const invalidate = useCaseInvalidation(projectCode);
  return useMutation({
    mutationFn: (input: ImportTestCasesInput) =>
      api.post<TestCaseImportResultDto>(`/projects/${projectCode}/test-cases/import`, input),
    onSuccess: invalidate,
  });
};
